import { Injectable } from '@angular/core';
import { config } from '../../config/appConfig';
import { HttpService } from './http.service';
import { concatMap, from, map, Observable, of, switchMap, toArray } from 'rxjs';
import {
  IEditorChaptersResponse,
  IPagesResponse,
  ICreatePageRequest,
  IPage,
  ITranslation,
  IEditorPageTranslationsResponse,
  IResponse,
  IPageResponse,
  ChapterType,
  ICreateBookRequest,
  IBook,
  ICreateChapterRequest,
  IChapter,
  ILayout,
  ILayoutList,
  IChapterUpdateResponse,
  IDeleteSubChapterResponse,
  IBookUpdateResponse,
  ISpelling,
  IChapterSpellings,
  BulkUpdatePageSpellingsRequest,
  BulkDeletePageSpellingsRequest,
  IBookAccessDetail,
  ISetBookAccessRequest,
} from '../models/admin';
import { HttpParams } from '@angular/common/http';

export enum AdminEndPoints {
  EditorBooks = '/admin/editor/books',
  EditorBooksWithPages = '/admin/editor/bookswithchapters',
  EditorLayout = '/admin/editor/layouts',
  EditorChaptersOfChapter = '/admin/editor/books/chapters',
  EditorPages = '/admin/editor/books/chapters',
  EditorBookReorder = '/admin/editor/books/reorder',
  EditorChapterReorder = '/admin/editor/books/chapters/reorder',
  EditorPageReorder = '/admin/editor/books/chapters/pages/reorder',
  SpellingsBulkUpdate = '/admin/editor/pages/spellings/bulk',
  authorize = '/user/authorize',
  refreshToken = '/user/refreshToken',
  getUser = '/user',
  updateUser = '/user',
  DeleteSubChapter = '/admin/editor/books/chapters/subChapters/',
}
@Injectable({
  providedIn: 'root',
})
export class AdminService {
  httpService: HttpService = new HttpService(config.adminServiceURL);

    postEditorBook(req: ICreateBookRequest): Observable<IResponse<IBook>> {
        return this.httpService.post(AdminEndPoints.EditorBooks, req)
    }
    postEditorBookReorder(sourceId: string | null, targetId: string | null): Observable<IResponse<string>> {
        return this.httpService.post(AdminEndPoints.EditorBookReorder, { SourceId: sourceId, TargetId: targetId })
    }
    postEditorPageReorder(sourceId: string | null, targetId: string | null): Observable<IResponse<string>> {
        return this.httpService.post(AdminEndPoints.EditorPageReorder, { SourceId: sourceId, TargetId: targetId })
    }
    postEditorChapterReorder(sourceId: string | null, targetId: string | null): Observable<IResponse<string>> {
        return this.httpService.post(AdminEndPoints.EditorChapterReorder, { SourceId: sourceId, TargetId: targetId })
    }
    postEditorSubChapterReorderAcrossParents(sourceParentId: string | undefined, targetParentId: string | undefined, sourceId: string | null, targetId: string | null): Observable<IResponse<string>> {
        return this.httpService.post(AdminEndPoints.EditorChapterReorder, { SourceParentId: sourceParentId, TargetParentId: targetParentId, ParentType: 2, SourceId: sourceId, TargetId: targetId })
    }
    postEditorPage(chapterid: string, req: ICreatePageRequest): Observable<IResponse<IPage>> {
        return this.httpService.post(AdminEndPoints.EditorChaptersOfChapter + `/${chapterid}/pages`, req)
    }
    releaseBook(bookId: string): Observable<IResponse<any>> {
        return this.httpService.post(AdminEndPoints.EditorBooks + `/${bookId}/release`);
    }
    getEditorBooks(pageNumber: number = 1, pageSize: number = 50): Observable<IPagesResponse<IBook>> {
        const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
        return this.httpService.get(AdminEndPoints.EditorBooks, params)
    }
    postEditorChapter(id: string, req: ICreateChapterRequest, forBook: boolean): Observable<IResponse<IChapter>> {
        if (forBook)
            return this.httpService.post(AdminEndPoints.EditorBooks + `/${id}/chapters`, req)
        else
            return this.httpService.post(AdminEndPoints.EditorChaptersOfChapter + `/${id}/subChapters`, req)
    }
    getEditorChapters(id: string, forBook: boolean): Observable<IEditorChaptersResponse> {
        const endpoint = forBook
            ? `${AdminEndPoints.EditorBooks}/${id}/chapters`
            : `${AdminEndPoints.EditorChaptersOfChapter}/${id}/subChapters`;
        return this.httpService.get(endpoint);
    }

  private getSubChapter(chapter: IChapter): Observable<IChapter> {
    return this.getEditorChapters(chapter.ChapterId, false).pipe(
      map((response) => {
        chapter.SubChapters = response.Value; // Attach fetched subchapters to the main chapter
        return chapter; // Return the updated chapter
      }),
    );
  }

  private getChaptersOfChapters(chapters: IChapter[]): Observable<IChapter[]> {
    return from(chapters).pipe(
      concatMap((chapter) => this.getSubChapter(chapter)), // Fetch and update each chapter
      toArray(), // Collect all updated chapters into an array
    );
  }

  getEditorChaptersWithSubchapters(
    bookid: string,
  ): Observable<IEditorChaptersResponse> {
    return this.getEditorChapters(bookid, true).pipe(
      switchMap((response) =>
        this.getChaptersOfChapters(response.Value).pipe(
          map((updatedChapters) => ({
            ...response, // Keep other properties of the response
            Value: updatedChapters, // Update the Value property with modified chapters
          })),
        ),
      ),
    );
  }

  getTopics(storyId: string): Observable<IPageResponse[]> {
    let pages: any = JSON.parse(
      localStorage.getItem(`story-${storyId}-pages`) as string,
    );
    return new Observable<IPageResponse[]>((subscriber) => {
      subscriber.next(
        (pages as any[]).map(
          (page) =>
            ({
              value: page,
              errors: [],
            }) as IPageResponse,
        ),
      );
      subscriber.complete();
    });

    // const pageURL = AdminEndPoints.EditorBooks + storyId + '/pages';
    // return this.httpService.get(pageURL);
  }
  getPages(storyId: string): Observable<IPageResponse[]> {
    let pages: any = JSON.parse(
      localStorage.getItem(`story-${storyId}-pages`) as string,
    );
    return new Observable<IPageResponse[]>((subscriber) => {
      subscriber.next(
        (pages as any[]).map(
          (page) =>
            ({
              value: page,
              errors: [],
            }) as IPageResponse,
        ),
      );
      subscriber.complete();
    });

    // const pageURL = AdminEndPoints.EditorBooks + storyId + '/pages';
    // return this.httpService.get(pageURL);
  }

  savePages(storyId: string, pages: any[]): Observable<any> {
    // For now, mock the save operation
    return new Observable((subscriber) => {
      localStorage.setItem(`story-${storyId}-pages`, JSON.stringify(pages));
      subscriber.next({ success: true });
      subscriber.complete();
    });
  }

  addBook(book: any): Observable<any> {
    return this.httpService.post(AdminEndPoints.EditorBooks, book);
  }
  private GetChapterRequest(
    type: ChapterType,
    title: string,
    subChapters: ICreateChapterRequest[],
  ): ICreateChapterRequest {
    return {
      GroupName: '',
      Type: type,
      Title: title,
      Description: '',
      AccessRights: [],
      AudioUrl: '',
      ThumbnailUrl: '',
      IconUrl: '',
      SubChapters: subChapters,
    };
  }

  private createChapters(
    bookId: string,
    chapters: ICreateChapterRequest[],
  ): Observable<any> {
    return from(chapters).pipe(
      concatMap((chapter) => this.createChapter(bookId, chapter)),
    );
  }

  private createChapter(
    bookId: string,
    chapter: ICreateChapterRequest,
  ): Observable<any> {
    return this.postEditorChapter(bookId, chapter, true).pipe(
      switchMap((response) => {
        const chapterId = response.Value.ChapterId;
        if (chapter.SubChapters && chapter.SubChapters.length > 0) {
          return this.createSubChapters(bookId, chapterId, chapter.SubChapters);
        }
        return of(null);
      }),
    );
  }

  private createSubChapters(
    bookId: string,
    chapterId: string,
    subChapters: ICreateChapterRequest[],
  ): Observable<any> {
    return from(subChapters).pipe(
      concatMap((subChapter) => this.createSubChapter(chapterId, subChapter)),
    );
  }

  private createSubChapter(
    chapterId: string,
    subChapter: ICreateChapterRequest,
  ): Observable<any> {
    return this.postEditorChapter(chapterId, subChapter, false);
  }

  addBookWithChapters(book: any): Observable<any> {
    let bookWithChapters: ICreateBookRequest = {
      ...book,
      Chapters: [
        this.GetChapterRequest(ChapterType.Preface, 'Preface', []),
        this.GetChapterRequest(ChapterType.Story1, 'Story1', []),
        this.GetChapterRequest(ChapterType.Story2, 'Story2', []),
        this.GetChapterRequest(ChapterType.Story3, 'Story3', []),
        this.GetChapterRequest(ChapterType.Story4, 'Story4', []),
        this.GetChapterRequest(ChapterType.VisualSeries1, 'VisualSeries1', [
          this.GetChapterRequest(ChapterType.Topic1, 'Topic1', []),
        ]),
        this.GetChapterRequest(ChapterType.VisualSeries2, 'VisualSeries2', [
          this.GetChapterRequest(ChapterType.Topic2, 'Topic2', []),
        ]),
        this.GetChapterRequest(ChapterType.VisualSeries3, 'VisualSeries3', [
          this.GetChapterRequest(ChapterType.Topic3, 'Topic3', []),
        ]),
        this.GetChapterRequest(ChapterType.VisualSeries4, 'VisualSeries4', [
          this.GetChapterRequest(ChapterType.Topic4, 'Topic4', []),
        ]),
        this.GetChapterRequest(ChapterType.VisualSeries5, 'VisualSeries5', [
          this.GetChapterRequest(ChapterType.Topic5, 'Topic5', []),
        ]),
        this.GetChapterRequest(ChapterType.Chat, 'Chat', []),
      ],
    };
    // Only Emit Once after all chapters (and their subchapters) are created.
    return this.postEditorBook(bookWithChapters).pipe(
      switchMap((response) => {
        const bookId = response.Value.BookId;
        return this.createChapters(bookId, bookWithChapters.Chapters).pipe(
          // Collect all chapter creation results
          toArray(),
          // Map to the original book creation response
          map(() => response),
        );
      }),
    );
  }

  updateBook(
    bookId: string,
    updatedFields: Partial<IBook>,
  ): Observable<IBookUpdateResponse> {
    return this.httpService.patch(
      `${AdminEndPoints.EditorBooks}/${bookId}`,
      updatedFields, // Send only modified fields
    );
  }

  deleteBook(bookId: string): Observable<any> {
    return this.httpService.delete(AdminEndPoints.EditorBooks + '/' + bookId);
  }

  /**
   * The access lists for every restricted book, in one request.
   *
   * Only books that actually carry grants are returned. A book missing from
   * the response has no grants at all, which means it is unrestricted and
   * visible to everyone - the caller must treat an absent entry as that,
   * rather than as a book whose access failed to load.
   */
  /**
   * Every extra permission the caller has been granted, keyed by book id.
   *
   * A delta, not a full picture: the global permissions carried by the token
   * are deliberately absent, because repeating an identical list under every
   * book id would make the payload grow with the catalogue while saying
   * nothing new. A book missing from the response means "no grants here",
   * which is not the same as no access - the token may still allow it.
   */
  getMyBookPermissions(): Observable<IResponse<Record<string, string[]>>> {
    return this.httpService.get(`${AdminEndPoints.EditorBooks}/my-permissions`);
  }

  getAllBookAccess(): Observable<IResponse<IBookAccessDetail[]>> {
    return this.httpService.get(`${AdminEndPoints.EditorBooks}/access`);
  }

  /**
   * The roles and members granted access to a book.
   *
   * Book access lives in the admin service, not the user service: the user
   * service only supplies the role and member pickers.
   */
  getBookAccess(bookId: string): Observable<IResponse<IBookAccessDetail>> {
    return this.httpService.get(
      `${AdminEndPoints.EditorBooks}/${bookId}/access`,
    );
  }

  /**
   * Replaces a book's entire access list.
   *
   * Replace-set, not merge: whatever is omitted is removed. The screen always
   * sends the full selection, so this matches how the row is edited.
   */
  setBookAccess(
    bookId: string,
    request: ISetBookAccessRequest,
  ): Observable<IResponse<IBookAccessDetail>> {
    return this.httpService.put(
      `${AdminEndPoints.EditorBooks}/${bookId}/access`,
      request,
    );
  }

  removeBookRoleAccess(
    bookId: string,
    roleId: number,
  ): Observable<IResponse<string>> {
    return this.httpService.delete(
      `${AdminEndPoints.EditorBooks}/${bookId}/access/roles/${roleId}`,
    );
  }

  removeBookMemberAccess(
    bookId: string,
    userId: string,
  ): Observable<IResponse<string>> {
    return this.httpService.delete(
      `${AdminEndPoints.EditorBooks}/${bookId}/access/members/${userId}`,
    );
  }

  updateChapter(
    chapterId: string,
    updatedFields: Partial<IChapter>,
  ): Observable<IChapterUpdateResponse> {
    return this.httpService.patch(
      `${AdminEndPoints.EditorChaptersOfChapter}/${chapterId}`,
      updatedFields, // Send only modified fields
    );
  }

  deleteChapter(chapterId: string): Observable<any> {
    return this.httpService.delete(
      `${AdminEndPoints.EditorChaptersOfChapter}/${chapterId}`,
    );
  }

  deleteSubChapter(
    subChapterId: string,
  ): Observable<IDeleteSubChapterResponse> {
    return this.httpService.delete(
      AdminEndPoints.DeleteSubChapter + subChapterId,
    );
  }

  //page related
  getEditorPage(
    pageId: string,
    layoutType: 'Web' | 'Mobile' | 'Tablet' | 'None' | 'All' = 'Web',
  ): Observable<IResponse<IPage>> {
    return this.httpService.get(
      `${AdminEndPoints.EditorPages}/pages/${pageId}?LayoutType=${layoutType}`,
    );
  }
  getEditorPages(
    chapterId: string,
    pageNumber: number = 1,
    pageSize: number = 10,
  ): Observable<IPagesResponse<IPage>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize);
    return this.httpService.get(
      AdminEndPoints.EditorPages + `/${chapterId}/pages`,
      params,
    );
  }

  addPage(chapterId: string, page: IPage): Observable<IResponse<IPage>> {
    return this.httpService.post(
      `${AdminEndPoints.EditorPages}/${chapterId}/pages`,
      page,
    );
  }

  updatePage(page: Partial<IPage> & {
    PageId: string;
    chapterId: string;
  }): Observable<any> {
    return this.httpService.patch(
      `${AdminEndPoints.EditorPages}/pages/${page.PageId}`,
      page,
    );
  }

  updatePageSpelling(payload: {
    PageId: string;
    Spellings: ISpelling[];
  }): Observable<any> {
    return this.httpService.patch(
      `${AdminEndPoints.EditorPages}/pages/${payload.PageId}/spelling`,
      {
        Spellings: JSON.stringify(payload.Spellings),
      },
    );
  }

  getChapterSpellings(
    chapterId: string,
  ): Observable<IResponse<IChapterSpellings>> {
    return this.httpService.get(
      `${AdminEndPoints.EditorPages}/${chapterId}/spellings`,
    );
  }

  bulkUpdatePageSpellings(
    request: BulkUpdatePageSpellingsRequest,
  ): Observable<IResponse<string>> {
    return this.httpService.post(
      `${AdminEndPoints.SpellingsBulkUpdate}`,
      request,
    );
  }

  bulkDeletePageSpellings(
    request: BulkDeletePageSpellingsRequest,
  ): Observable<IResponse<string>> {
    return this.httpService.post(
      `${AdminEndPoints.SpellingsBulkUpdate}`,
      request,
    );
  }

  deletePageSpelling(pageId: string): Observable<any> {
    return this.httpService.delete(
      `${AdminEndPoints.EditorPages}/pages/${pageId}/spelling`,
    );
  }

  deletePage(chapterId: string, pageId: string): Observable<IResponse<string>> {
    return this.httpService.delete(
      `${AdminEndPoints.EditorPages}/pages/${pageId}`,
    );
  }

  // translation related methods
  getEditorPageTranslations(
    pageId: string,
    languageCode: string | null = null,
  ): Observable<IEditorPageTranslationsResponse> {
    let params = new HttpParams();

    if (languageCode !== null) {
      params = params.set('language', languageCode);
    }

    return this.httpService.get(
      `/admin/editor/pages/${pageId}/translations`,
      params,
    );
  }

  addTranslation(
    pageId: string,
    translation: ITranslation,
  ): Observable<IResponse<ITranslation>> {
    return this.httpService.post(
      `/admin/editor/pages/${pageId}/translations?`,
      translation,
    );
  }
  updateTranslation(translation: ITranslation): Observable<any> {
    return this.httpService.patch(
      `/admin/editor/translations/${translation.TranslationID}`,
      translation,
    );
  }
  deleteTranslation(translationId: string): Observable<any> {
    return this.httpService.delete(
      `/admin/editor/translations/${translationId}`,
    );
  }

  //layout related method
  addLayout(layout: ILayout): Observable<any> {
    return this.httpService.post(AdminEndPoints.EditorLayout, layout);
  }
  getLayouts(): Observable<IResponse<ILayoutList[]>> {
    return this.httpService.get(AdminEndPoints.EditorLayout);
  }
  getLayout(id: string): Observable<IResponse<ILayout>> {
    return this.httpService.get(AdminEndPoints.EditorLayout + `/${id}`);
  }
  updateLayout(layoutId: string, layout: ILayout): Observable<any> {
    return this.httpService.patch(
      AdminEndPoints.EditorLayout + `/${layoutId}`,
      layout,
    );
  }
  deleteLayout(id: string): Observable<any> {
    return this.httpService.delete(AdminEndPoints.EditorLayout + `/${id}`);
  }
}
